import React, { useState, useCallback } from "react";
import { Typography, Button, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { OrganizationSearchDialog } from "@/shared/components/OrganizationSearchDialog";
import type { DaDataOrganizationSuggestionType } from "@/shared/types/common";
import { useForm } from "react-final-form";

interface CompanyTitleFieldProps {
  label?: string;
  onOrganizationSelect?: (
    organization: DaDataOrganizationSuggestionType
  ) => void;
  searchButtonText?: string;
  editData?: any;
  allValues?: any;
  fieldMapping?: {
    legalName?: string;
    inn?: string;
    kpp?: string;
    ogrn?: string;
    representative?: string;
    representativePosition?: string;
    representationBasis?: string;
  };
}

export const CompanyTitleField: React.FC<CompanyTitleFieldProps> = ({
  label = "Карточка компании",
  onOrganizationSelect,
  searchButtonText = "Найти организацию",
  fieldMapping = {},
}) => {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const form = useForm();

  const handleOpenSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(true);
  }, []);

  const handleCloseSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(false);
  }, []);

  const handleSelectOrganization = useCallback(
    (organization: DaDataOrganizationSuggestionType) => {
      const { data } = organization;

      // Маппинг полей DaData в формат компании
      const companyFields = {
        [fieldMapping.legalName || "legalName"]: data.name.full_with_opf || "",
        [fieldMapping.inn || "inn"]: data.inn || "",
        [fieldMapping.kpp || "kpp"]: data.kpp || "",
        [fieldMapping.ogrn || "ogrn"]: data.ogrn || "",
        [fieldMapping.representative || ""]: data.management?.name || "",
        [fieldMapping.representativePosition || "representativePosition"]:
          data.management?.post || "",
        [fieldMapping.representationBasis || "representationBasis"]: "", // Не передается через DaData
      };

      // Обновляем поля компании в форме
      Object.entries(companyFields).forEach(([fieldName, value]) => {
        form.change(fieldName, value);
      });

      // Вызываем callback если передан
      if (onOrganizationSelect) {
        onOrganizationSelect(organization);
      }
    },
    [form, fieldMapping, onOrganizationSelect]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6">{label}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SearchIcon />}
          onClick={handleOpenSearchDialog}
          sx={{
            fontWeight: 400,
          }}
        >
          {searchButtonText}
        </Button>
      </Box>

      <OrganizationSearchDialog
        open={isSearchDialogOpen}
        onClose={handleCloseSearchDialog}
        onSelect={handleSelectOrganization}
        title="Поиск организации"
      />
    </>
  );
};
